import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate, useParams } from "react-router-dom";
import React, { useContext, useEffect, useState } from "react";
import { Modal, Offcanvas, Tab, Tabs } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import Context from "../../../store/context";
import ConfigurationTab from "./ConfigurationTab";
import EventsTabs from "./EventsTabs";
import getSurveyData from "./helpers/getSurveyData";
import checkIfDynamicContactUsed from "./helpers/checkIfDynamicContactUsed";
import ContactsAndGroups from "../../Components/modals/ContactsAndGroups";
import {
  selectEventHandlers,
  selectParentWorkflows,
  selectWorkflow,
  setEntryWorkflow,
  setWorkflow,
} from "../../../store/slices/projectSlice";
import { selectSendLists } from "../../../store/slices/sendListsSlice";
import HelpButton from "../../../HelpContent/HelpButton";
import Errors from "../../Components/Errors";
import {
  faMaximize,
  faRightToBracket,
} from "@fortawesome/free-solid-svg-icons";

export default function WorkflowModal() {
  const navigate = useNavigate();
  const params = useParams();

  const { isModalSidebar, setIsModalSidebar, engine } = useContext(Context);
  const sendLists = useSelector(selectSendLists);
  const parentWorkflows = useSelector(
    selectParentWorkflows(params.workflowUUID)
  );

  const node = engine.model.getNode(params.workflowUUID);

  const [show, setShow] = useState(true);

  const [state, setState] = useState(
    useSelector(selectWorkflow(params.workflowUUID))
  );

  const eventHandlers = useSelector(selectEventHandlers);
  const storeDispatch = useDispatch();

  const dispatch = (action) => {
    const newState = (() => {
      switch (action.type) {
        case "edit-label":
          return { ...state, Label: action.data };
        case "set-entry": {
          storeDispatch(setEntryWorkflow(state.UUID));
          return { ...state, IsEntrypoint: true };
        }
        case "edit-survey":
          return { ...state, SurveyJSON: action.data };
        case "edit-survey-theme":
          return { ...state, SurveyThemeUUID: action.data };
        case "update-listener":
          return {
            ...state,
            EventListeners: state.EventListeners.map((listener) => {
              if (listener.UUID === action.data.UUID) {
                return { ...action.data, Order: listener.Order };
              }
              return listener;
            }),
          };
        case "add-listener": {
          const uniqueId = uuidv4();
          setTimeout(() => {
            navigate(
              `/library/logic/builder/${params.projectID}/workflow/${params.workflowUUID}/event/${params.event}/listener/${uniqueId}`
            );
          }, 50);
          return {
            ...state,
            EventListeners: [
              ...(state.EventListeners || []),
              {
                UUID: uniqueId,
                Order:
                  Math.max(
                    -1,
                    ...(state.EventListeners || [])
                      .filter((listener) => listener.EventType === action.event)
                      .map((listener) => listener.Order)
                  ) + 1, // find the highest order
                EventType: action.event || "start:runtime",
              },
            ],
          };
        }

        case "remove-listener": {
          // find the event handler for the listener
          const eventHandler = eventHandlers.find(
            (eh) =>
              eh.UUID ===
              state.EventListeners.find((l) => action.listener.UUID === l.UUID)
                ?.EventHandlerUUID
          );
          // if event handler is module, remove links first
          if (eventHandler?.IsModule) {
            eventHandler?.Rules?.forEach((r) =>
              r.Actions.forEach((a) => {
                const port = node?.getPort(`${action.listener.UUID}-${a.UUID}`);
                if (port) {
                  port.options.isModule = false;
                  Object.values(port.getLinks()).forEach((link) =>
                    link.remove()
                  );
                }
              })
            );
          }
          // check for links connected to port
          if (
            eventHandler?.Rules?.find((r) =>
              r.Actions?.find(
                (a) =>
                  !eventHandler?.IsModule &&
                  a.Type === "start:runtime" &&
                  a.TypeUUID
              )
            )
          ) {
            toast.error(
              "Port is connected. Listener can not be removed. Disconnect port first."
            );
            return state;
          }

          return {
            ...state,
            EventListeners: state.EventListeners.filter(
              (l) => action.listener.UUID !== l.UUID // remove the listener
            ).map((l) => {
              // reorder remaining listeners
              if (
                l.EventType === action.listener.EventType &&
                l.Order > action.listener.Order
              ) {
                return { ...l, Order: l.Order - 1 };
              }
              return l;
            }),
          };
        }
        case "listener-right":
          return {
            ...state,
            EventListeners: state.EventListeners.map((listener) => {
              if (listener.EventType === action.event) {
                if (
                  listener.Order === action.order + 1 &&
                  listener.UUID !== action.listenerId
                ) {
                  return { ...listener, Order: action.order };
                }
                if (listener.UUID === action.listenerId) {
                  return { ...listener, Order: action.order + 1 };
                }
              }
              return listener;
            }),
          };
        case "listener-left":
          return {
            ...state,
            EventListeners: state.EventListeners.map((listener) => {
              if (listener.EventType === action.event) {
                if (
                  listener.Order === action.order - 1 &&
                  listener.UUID !== action.listenerId
                ) {
                  return { ...listener, Order: action.order };
                }
                if (listener.UUID === action.listenerId) {
                  return { ...listener, Order: action.order - 1 };
                }
              }
              return listener;
            }),
          };
        case "message-template":
          return {
            ...state,
            MessageTemplateUUID: action.MessageTemplateUUID,
          };
        case "contact-set":
          return {
            ...state,
            ContactSetUUID: "yes",
          };
        default:
          return state;
      }
    })();
    setState(newState);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      storeDispatch(setWorkflow(state));
    }, 50);
    return () => clearTimeout(timeout);
  }, [state]);

  const saveForm = () =>
    dispatch({
      type: "edit-survey",
      data: getSurveyData(state, eventHandlers),
    });

  const handleClose = () => {
    setShow(false);
    saveForm();
  };

  const handleExited = () => {
    navigate(`/library/logic/builder/${params.projectID}`);
  };

  const handleEntered = () => saveForm();

  const handleSetEntry = () => {
    dispatch({ type: "set-entry" });
  };

  const checkIfHasSendLIst = () => {
    const sendList = sendLists.find((sl) => sl.uuid === state.UUID);
    if (
      sendList &&
      (sendList.contacts?.length > 0 ||
        sendList.contactSets?.length > 0 ||
        sendList.filters?.length > 0)
    ) {
      return true;
    }
    return false;
  };

  let Container = Modal;
  if (isModalSidebar) {
    Container = Offcanvas;
  }

  // if workflow doesn't exist
  if (state === undefined) {
    return (
      <Container
        show={params.workflowUUID}
        onHide={() => navigate(`/library/logic/builder/${params.projectID}`)}
      >
        <Container.Body>Workflow not found!</Container.Body>
      </Container>
    );
  }

  if (state) {
    return (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div
        onMouseDown={(e) => e.stopPropagation()}
        // onMouseMove={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
      >
        <Container
          show={show}
          size="xxl"
          onHide={handleClose}
          onExited={handleExited}
          onEntered={handleEntered}
          fullscreen="xxl-down"
          scrollable
          style={isModalSidebar ? { width: "40vw" } : {}}
          placement={isModalSidebar ? "end" : undefined}
        >
          <Container.Header closeButton>
            <Container.Title>
              <i className="icon fa fa-cog" /> Configure Options for{" "}
              {state.Label}
            </Container.Title>
            <span className="flex-grow-1" />
            {checkIfDynamicContactUsed(state, eventHandlers) ? (
              <span className="badge bg-white text-purple badge-pill px-2 mx-2">
                Contact context created
              </span>
            ) : (
              ""
            )}
            {state.MessageTemplateUUID ? (
              <span className="badge bg-white text-purple badge-pill px-2 mx-2">
                Has message template
              </span>
            ) : (
              ""
            )}
            {checkIfHasSendLIst() ? (
              <span className="badge bg-white text-purple badge-pill px-2 mx-2">
                Workflow contacts
              </span>
            ) : (
              ""
            )}
            {state.IsEntrypoint ? (
              <span className="badge bg-white text-purple badge-pill px-2 mx-2">
                First workflow
              </span>
            ) : (
              <button
                type="button"
                className="btn btn-link text-white"
                onClick={handleSetEntry}
                disabled={parentWorkflows.length > 0}
              >
                set as first workflow
              </button>
            )}
            <button
              type="button"
              className="btn btn-outline-secondary border-0"
              onClick={() => setIsModalSidebar((prev) => !prev)}
            >
              {isModalSidebar ? (
                <FontAwesomeIcon icon={faMaximize} title="Show as modal" />
              ) : (
                <FontAwesomeIcon
                  icon={faRightToBracket}
                  title="Show as sidebar"
                />
              )}
            </button>
          </Container.Header>
          <Container.Body>
            <Tabs
              id="workflow-tabs"
              className="mb-3"
              mountOnEnter
              activeKey={params.workflowTab}
              onSelect={(key) => {
                saveForm();
                if (key === "configuration") {
                  navigate(
                    `/library/logic/builder/${params.projectID}/workflow/${state.UUID}/configuration`
                  );
                } else if (key === "send-list") {
                  navigate(
                    `/library/logic/builder/${params.projectID}/workflow/${state.UUID}/send-list`
                  );
                } else {
                  const listenerUUID = state.EventListeners?.find(
                    (listener) =>
                      listener.EventType === "runtime:start" &&
                      listener.Order === 0
                  )?.UUID;
                  navigate(
                    `/library/logic/builder/${params.projectID}/workflow/${state.UUID}/event/runtime:start/listener/${listenerUUID}`
                  );
                }
              }}
            >
              <Tab
                title={
                  <span>
                    Events <HelpButton helpKey="components-events" />
                  </span>
                }
                eventKey="events"
              >
                <EventsTabs state={state} dispatch={dispatch} />
              </Tab>
              <Tab
                title={
                  <span>
                    Configuration
                    <Errors
                      filterFn={(e) =>
                        e.workflow?.UUID === state?.UUID && e.configuration
                      }
                      exclamationMarkOnly
                      linkDisabled
                    />
                    <HelpButton helpKey="components-configurations" />
                  </span>
                }
                eventKey="configuration"
              >
                <ConfigurationTab state={state} dispatch={dispatch} />
              </Tab>
              <Tab
                title={
                  <span>
                    Contacts and groups
                    <Errors
                      filterFn={(e) =>
                        e.workflow?.UUID === state?.UUID && e.sendList
                      }
                      exclamationMarkOnly
                      linkDisabled
                    />
                    <HelpButton helpKey="components-contacts-and-groups" />
                  </span>
                }
                eventKey="send-list"
              >
                <ContactsAndGroups
                  uuid={state.UUID}
                  uuidType="workflow"
                  onSave={() => dispatch({ type: "contact-set" })}
                />
              </Tab>
            </Tabs>
          </Container.Body>
        </Container>
      </div>
    );
  }
}
